import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Calendar } from '@/components/ui/calendar';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { CalendarIcon, Ban, CheckCircle2, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import { supabase } from '@/integrations/supabase/client';

interface AvailabilityRecord {
  id: string;
  date: string;
  is_available: boolean;
  reason: string | null;
}

export function GutterAvailabilityTab() {
  const [selectedDates, setSelectedDates] = useState<Date[]>([]);
  const [reason, setReason] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [availabilityRecords, setAvailabilityRecords] = useState<AvailabilityRecord[]>([]);
  const [fetchingRecords, setFetchingRecords] = useState(true);

  useEffect(() => {
    fetchAvailability();
  }, []);

  const fetchAvailability = async () => {
    setFetchingRecords(true);
    try {
      const { data, error } = await supabase
        .from('gutter_cleaning_availability')
        .select('*')
        .order('date', { ascending: true });

      if (error) throw error;

      setAvailabilityRecords(data || []);
    } catch (error: any) {
      console.error('Error fetching availability:', error);
      toast.error('Failed to load availability records');
    } finally {
      setFetchingRecords(false);
    }
  };

  const handleBlockDates = async () => {
    if (selectedDates.length === 0) {
      toast.error('Please select at least one date to block');
      return;
    }

    setIsLoading(true);
    try {
      const records = selectedDates.map(date => ({
        date: format(date, 'yyyy-MM-dd'),
        is_available: false,
        reason: reason.trim() || 'Date blocked by admin',
      }));

      const { error } = await supabase
        .from('gutter_cleaning_availability')
        .upsert(records, { onConflict: 'date' });

      if (error) throw error;

      toast.success(`Blocked ${selectedDates.length} date(s)`);
      setSelectedDates([]);
      setReason('');
      await fetchAvailability();
    } catch (error: any) {
      console.error('Error blocking dates:', error);
      toast.error('Failed to block dates');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUnblockDates = async () => {
    if (selectedDates.length === 0) {
      toast.error('Please select at least one date to unblock');
      return;
    }

    setIsLoading(true);
    try {
      const records = selectedDates.map(date => ({
        date: format(date, 'yyyy-MM-dd'),
        is_available: true,
        reason: null,
      }));

      const { error } = await supabase
        .from('gutter_cleaning_availability')
        .upsert(records, { onConflict: 'date' });

      if (error) throw error;

      toast.success(`Unblocked ${selectedDates.length} date(s)`);
      setSelectedDates([]);
      setReason('');
      await fetchAvailability();
    } catch (error: any) {
      console.error('Error unblocking dates:', error);
      toast.error('Failed to unblock dates');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteRecord = async (id: string, date: string) => {
    try {
      const { error } = await supabase
        .from('gutter_cleaning_availability')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast.success(`Removed availability record for ${date}`);
      await fetchAvailability();
    } catch (error: any) {
      console.error('Error deleting record:', error);
      toast.error('Failed to delete record');
    }
  };

  const blockedDates = availabilityRecords
    .filter(r => !r.is_available)
    .map(r => new Date(r.date));

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CalendarIcon className="h-5 w-5" />
            Manage Gutter Cleaning Availability
          </CardTitle>
          <CardDescription>
            Block or unblock dates for gutter cleaning bookings
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid gap-6 lg:grid-cols-2">
            {/* Calendar Section */}
            <div className="space-y-4">
              <div>
                <Label className="text-base font-semibold mb-3 block">Select Dates</Label>
                <Calendar
                  mode="multiple"
                  selected={selectedDates}
                  onSelect={(dates) => setSelectedDates(dates || [])}
                  disabled={(date) => date < new Date()}
                  className="rounded-md border"
                  modifiers={{
                    blocked: blockedDates,
                  }}
                  modifiersStyles={{
                    blocked: {
                      backgroundColor: '#fecaca',
                      color: '#991b1b',
                    },
                  }}
                />
                {selectedDates.length > 0 && (
                  <p className="mt-3 text-sm text-muted-foreground">
                    Selected: {selectedDates.length} date(s)
                  </p>
                )}
              </div>
            </div>

            {/* Actions Section */}
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="reason">Reason (Optional)</Label>
                <Textarea
                  id="reason"
                  placeholder="e.g., Holiday, Maintenance, Fully booked..."
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={3}
                />
              </div>

              <div className="flex flex-col gap-2">
                <Button
                  onClick={handleBlockDates}
                  disabled={isLoading || selectedDates.length === 0}
                  variant="destructive"
                  className="w-full"
                >
                  {isLoading ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Ban className="h-4 w-4 mr-2" />
                  )}
                  Block Selected Dates
                </Button>

                <Button
                  onClick={handleUnblockDates}
                  disabled={isLoading || selectedDates.length === 0}
                  variant="outline"
                  className="w-full"
                >
                  {isLoading ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                  )}
                  Unblock Selected Dates
                </Button>
              </div>

              <div className="bg-muted/50 p-4 rounded-md">
                <h4 className="font-medium mb-2">Legend</h4>
                <div className="space-y-1 text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-red-200 border border-red-800 rounded"></div>
                    <span>Blocked dates</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-background border rounded"></div>
                    <span>Available dates</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Current Blocked Dates */}
      <Card>
        <CardHeader>
          <CardTitle>Blocked Dates</CardTitle>
          <CardDescription>
            Dates that are currently unavailable for booking
          </CardDescription>
        </CardHeader>
        <CardContent>
          {fetchingRecords ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="space-y-2">
              {availabilityRecords.filter(r => !r.is_available).length === 0 ? (
                <p className="text-muted-foreground text-center py-4">
                  No blocked dates
                </p>
              ) : (
                availabilityRecords
                  .filter(r => !r.is_available)
                  .map((record) => (
                    <div
                      key={record.id}
                      className="flex items-center justify-between p-3 border rounded-md"
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">
                            {format(new Date(record.date), 'PPP')}
                          </span>
                          <Badge variant="destructive">Blocked</Badge>
                        </div>
                        {record.reason && (
                          <p className="text-sm text-muted-foreground mt-1">
                            {record.reason}
                          </p>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteRecord(record.id, record.date)}
                      >
                        Remove
                      </Button>
                    </div>
                  ))
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
